<template>
  <div
    v-if="renderComponent"
    id="make-an-order"
    class="make-order-container pt-3"
  >
    <div v-if="brand" class="order-form">
      <div class="order-available" v-if="type === 'buy'">
        <label class="order-available__label">
          {{ $t("account.available-amount") }}
        </label>
        <div class="order-available__content">
          <span class="order-available__content__amount">
            {{ localBalanceValue | currency }}
          </span>
          <span class="order-available__content__currency"> </span>
        </div>
      </div>
      <div
        class="order-available"
        v-else-if="type === 'sell' && brandShareBalance !== null"
      >
        <label class="order-available__label">
          {{ $t("account.available") }}:&nbsp;
        </label>
        <div class="order-available__content">
          <span class="order-available__content__amount">
            {{
              brandShareBalance.value - (brandShareBalance.hold || 0) ||
              0 | currency("no-precision", "")
            }}
          </span>
          <span class="order-available__content__currency"> ш </span>
        </div>
      </div>
      <order-input
        size="sm"
        :placeholder="$t('order.price')"
        v-if="orderType !== 'current'"
        v-model.number="price"
        :trade="orderType !== 'current' ? true : false"
        @blur="$v.orderModel.unit_price.$touch()"
        :state="
          $v.orderModel.unit_price.$dirty
            ? !$v.orderModel.unit_price.$anyError
            : null
        "
      >
        <template v-slot:suffix>
          <p>MNT</p>
        </template>
      </order-input>

      <div
        v-if="orderType === 'current'"
        class="p-2 text-right w-100 market-order__label"
      >
        <span>ЗАХ ЗЭЭЛИЙН ҮНЭ</span>
      </div>

      <order-input
        :placeholder="$t('order.quantity')"
        v-model.number="quantity"
        decimal=""
        :precision="0"
        :trade="orderType === 'current' ? false : true"
        @blur="$v.orderModel.quantity.$touch()"
        :state="
          $v.orderModel.quantity.$dirty
            ? !$v.orderModel.quantity.$anyError
            : null
        "
      >
        <template v-slot:suffix>
          <p>{{ brand.symbol | symbol }}</p>
        </template>
      </order-input>
      <div class="percentage-buttons">
        <div
          class="percentage-button"
          v-for="(item, index) in percentages"
          :key="`percentage-${type}-${index}`"
          @click.stop="handleChangeAmountByButton(item)"
        >
          {{ item * 100 }}%
        </div>
      </div>
      <div class="order-available">
        <label class="order-available__label">
          {{ $t("order.duration.title") }}
        </label>
      </div>
      <b-form-select
        class="mb-10"
        :options="filteredDurationOptions"
        v-model="orderModel.time_in_force"
        @blur="$v.orderModel.time_in_force.$touch()"
        :state="
          $v.orderModel.time_in_force.$dirty
            ? !$v.orderModel.time_in_force.$anyError
            : null
        "
      />
      <date-picker
        block
        class="mb-10"
        v-if="orderModel.time_in_force === 6"
        :min-date="new Date()"
        :max-date="max"
        @change="changeDate"
      />
      <div class="order-total">
        <label class="order-total__label">
          {{ $t("order.total-amount") }}
        </label>
        <div class="order-total__content">
          <span class="order-total__content__amount">
            {{ orderType === "current" ? "&asymp;" : null }}
            {{ calculations | currency("", "") }}
          </span>
          <span class="order-total__content__currency"> MNT </span>
        </div>
      </div>
      <ic-button
        v-if="type === 'buy'"
        id="order-button-buy"
        size="tn"
        block
        @click="makeOrder('buy')"
      >
        {{ $t("order.buy") }}
      </ic-button>
      <ic-button
        v-else-if="type === 'sell'"
        id="order-button-sell"
        size="tn"
        color="danger"
        block
        @click="makeOrder('sell')"
        :disabled="brandShareBalance.value == 0"
      >
        {{ $t("order.sell") }}
      </ic-button>
      <ic-dialog :show="showOrderModal">
        <div class="dialog-box" style="border-color: transparent;">
          <make-order
            :order-info="this.orderInfo"
            :buy-sell-type="buySellType"
            @closeModal="closeModal"
            @sendRequest="sendRequest"
          ></make-order>
        </div>
      </ic-dialog>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line
import _ from "lodash";
import moment from "moment";
import { validationMixin } from "vuelidate";
import { mapState, mapGetters, mapActions } from "vuex";
import { required, integer, numeric } from "vuelidate/lib/validators";
import api from "../../api/stoTrade";
import OrderInput from "../OrderInput";
import DatePicker from "../DatePicker";
import MakeOrder from "./MakeOrder.vue";

// const expireTimeValidation = (value) => moment(value).isValid();
export default {
  name: "OrderForm",
  mixins: [validationMixin],
  components: {
    OrderInput,
    DatePicker,
    MakeOrder,
  },
  props: {
    type: String,
    orderType: String,
    selectedOrder: {
      type: Object,
      default: null,
    },
  },
  validations: {
    orderModel: {
      unit_price: {
        valid() {
          return (
            this.orderModel.order_type === 1 ||
            (this.orderModel.order_type === 2 && !!this.orderModel.unit_price)
          );
        },
      },
      quantity: { required, integer },
      time_in_force: {
        valid() {
          // TODO: eniig shalgaj uzex
          return (
            this.orderModel.order_type === 1 ||
            !this.isUndefinedOrNull(this.orderModel.time_in_force)
          );
        },
      },
      expire_time: {
        expireTimeValidation() {
          return (
            this.orderModel.time_in_force !== 6 ||
            moment(this.orderModel.expire_time).isValid()
          );
        },
      },
    },
  },
  data() {
    const maxDate = moment().add(1, "month").toDate();
    return {
      renderComponent: true,
      max: maxDate,
      isLoading: false,
      isConditional: true,
      durationOption: [
        { value: 0, text: this.$t("order.duration.today") },
        { value: 9, text: this.$t("order.duration.this-week") },
        { value: 6, text: this.$t("helper.date") },
      ],
      percentages: [0.25, 0.5, 0.75, 1],
      orderModel: {
        order_type: 2,
        unit_price: null,
        quantity: undefined,
        time_in_force: 0,
        expire_time: "",
      },
      buySellType: null,
      showOrderModal: false,
    };
  },
  created() {
    // this.$store.dispatch('fetchLocalBalance')
    // this.$store.dispatch('fetchShareBalance')
  },
  mounted() {
    this.setUnitPrice();
  },
  updated() {
    this.setUnitPrice();
  },
  methods: {
    changeDate(date) {
      this.orderModel.expire_time = date;
    },
    forceRerender() {
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
      });
    },
    closeModal() {
      this.showOrderModal = false;
    },
    async cent() {
      if (!this.connected || this.user === null) {
        console.log("not connected yet.");
        return;
      }

      if (this.user)
        this.instance.subscribe(`order-state#${this.user.id}`, (msg) => {
          // Захиалгын төлөв өөрчлөгдсөн бол номинал данс болон үлдэгдлийг авах
          this.$store.dispatch("fetchLocalBalance");
          this.$store.dispatch("fetchShareBalance");
        });
    },
    setPriceBasedOnType() {
      // ЯГ ИНГЭЖ ЯВБАЛ АСУУДАЛ ГАРАХААР БАЙНА
      // if (this.type === 'buy') {
      //   this.orderModel.unit_price =
      //       this.brand.high_price ?? this.brand.closing_price
      // } else {
      //   this.orderModel.unit_price =
      //       this.brand.low_price ?? this.brand.closing_price
      // }
      // if (this.orderModel.unit_price == 0)

      this.orderModel.unit_price = this.brand.closing_price;
    },
    setUnitPrice() {
      if (this.orderType === "current") {
        this.setPriceBasedOnType();
      } else {
        // this.orderModel.unit_price = null
      }
    },
    async makeOrder(type) {
      this.buySellType = type;
      this.$v.orderModel.$touch();
      if (this.$v.orderModel.$invalid || this.$v.orderModel.$error) {
        console.log("error : ...");
        this.$bvToast.toast(
          "Захиалгын мэдээлэл алдаатай байна, дахин шалгана уу",
          { title: "Алдаа", variant: "danger" },
        );
        return;
      }
      this.showOrderModal = true;
    },

    async sendRequest(fee) {
      this.showOrderModal = false;
      this.isLoading = true;
      try {
        const payload = {
          side: this.buySellType === "buy" ? 1 : 2,
          fee: fee || this.orderInfo?.commission || 0,
          ...this.orderInfo,
        };
        let data;
        if (this.buySellType === "buy") {
          data = await api.buy(payload);
        } else {
          data = await api.sell(payload);
        }
        if (data?.data?.success === false) {
          this.$bvToast.toast(data.data.message, {
            title: "Захиалга амжилтгүй",
            variant: "warning",
          });
        } else {
          this.$bvToast.toast(this.$t("helper.slotted-pending"), {
            title: this.$t("helper.success"),
            variant: "success",
          });
          await this.$store.dispatch("fetchOpenOrders");
        }
      } catch (e) {
        console.error(e);
      }
      this.isLoading = false;
    },
  },
  watch: {
    brand: {
      handler: "forceRerender",
      deep: true,
    },
    // brand: {
    //   handler(brand) {
    //     console.log(brand)
    //     // if (!value) {
    //     //   this.hideSubMenu()
    //     // }
    //     // return value
    //   },
    // },
    "orderModel.order_type": {
      handler() {
        let currentType = this.orderModel.order_type;
        this.resetModel();
        this.orderModel.order_type = currentType;
      },
    },
    orderType() {
      this.setUnitPrice();
    },
    brandShareBalance() {
      this.clearInputFields();
    },
    selectedOrder(newVal, _) {
      if (
        (newVal.type === "0" && this.type == "sell") ||
        (newVal.type === "1" && this.type == "buy")
      ) {
        this.orderModel.unit_price = newVal.price;
      } else {
        this.orderModel = {
          order_type: this.orderModel.order_type,
          unit_price: 0,
          quantity: 0,
          time_in_force: 0,
          expire_time: "",
        };
      }
    },
  },
  computed: {
    ...mapState("brand", ["brand"]),
    ...mapState("user", ["user"]),
    ...mapState("cent", ["token", "instance", "connected"]),
    ...mapGetters([
      "localBalance",
      "shareBalance",
      "feePercent",
      "localBalanceLoading",
      "shareBalanceLoading",
    ]),
    ...mapGetters("trade", ["latestTrade"]),
    ...mapGetters("user", ["fees"]),
    filteredDurationOptions() {
      return [
        {
          value: 0,
          text: this.$t("order.duration.today"),
        },
        {
          value: 9,
          text: this.$t("order.duration.this-week"),
          disabled: this.brand.exchange_server_code === "ubx",
        },
        {
          value: 6,
          text: this.$t("helper.date"),
          disabled: this.brand.exchange_server_code === "ubx",
        },
      ];
    },
    feeList() {
      if (this.brand.exchange_server_code === "ubx") return this.fees.ubx;
      return this.fees.mse ?? [];
    },
    unitPrice() {
      let unitPrice = this.orderModel.unit_price;
      if (this.orderType === "current") {
        unitPrice = parseFloat(
          this.latestTrade ? this.latestTrade : this.brand.closing_price,
        );
        const margin = (unitPrice * 4.99) / 100;
        return unitPrice + margin;
      }
      return unitPrice;
    },
    calculations() {
      if (this.orderInfo) {
        console.debug("up: ", this.unitPrice);
        console.debug("qt: ", this.orderModel.quantity);
        return this.type === "sell"
          ? this.unitPrice * this.orderModel.quantity -
              this.orderInfo.commission
          : this.unitPrice * this.orderModel.quantity +
              this.orderInfo.commission;
      }
    },
    canPay() {
      if (this.orderInfo) {
        return (
          this.unitPrice * this.orderModel.quantity +
            this.orderInfo.commission <=
          this.localBalanceValue
        );
      }
    },
    nextWeek() {
      // TODO: tsag xaruulax shaardlagatai bol tsagiig format value deer nemj ugno HH:MM:ss
      return moment().add(7, "days").format("YYYY-MM-DD");
    },
    brand() {
      return this.$store.state.brand.brand;
    },
    brandShareBalance() {
      if (this.shareBalance) {
        const brandBalance = this.shareBalance.find(
          (e) => e.symbol === this.brand.symbol,
        );
        if (brandBalance) {
          return brandBalance;
        } else {
          return {
            symbol: this.brand.symbol,
            available: 0,
          };
        }
      } else {
        return {
          symbol: this.brand.symbol,
          available: 0,
        };
      }
    },
    localBalanceValue() {
      if (
        this.localBalance === null ||
        this.localBalance === undefined ||
        this.localBalance === ""
      ) {
        return 0;
      } else {
        return this.localBalance.availableBalance;
      }
    },
    /**
     * Захиалгын шимтгэлийн төрлийг буцаах
     * Хэрэв хувьцаа байвал '0' утга буцаана
     * Үгүй бөгөөд зарах захиалга бол 'B', харин авах захиалга бол 'S'
     * @returns {string}
     */
    feeType() {
      return `${this.brand.stype}${
        this.brand.stype === 1 ? "0" : this.buySellType === "buy" ? "B" : "S"
      }`;
    },
    orderInfo() {
      // if (this.trades && Array.isArray(this.trades)) {
      //   console.log('last updated...');
      //   console.log(this.trades.reverse()[0]);
      // }
      if (this.feePercent) {
        let order_amount =
          this.orderType !== "current"
            ? this.orderModel.unit_price * this.orderModel.quantity
            : this.unitPrice * this.orderModel.quantity;
        // let commission = (order_amount / 100) * this.feePercent[this.feeType]
        console.log(this.feeList);
        const fee = this.feeList.find(
          (fee) =>
            fee.stype === this.brand.stype &&
            fee.side === (this.type === "buy" ? 1 : 2),
        );
        console.log(fee);
        const userFee = fee
          ? Number(fee.broker_fee) + Number(fee.transaction_fee)
          : 0;
        console.log(userFee);
        let commission = userFee
          ? (order_amount / 100) * userFee
          : (order_amount / 100) * this.feePercent[this.feeType];
        commission = commission.toFixed(2);
        return {
          symbol: this.brand.symbol,
          exchange_server_code: this.brand.exchange_server_code,
          stype: this.brand.stype,
          quantity: this.orderModel.quantity,
          unit_price: this.unitPrice,
          order_type: this.orderType === "current" ? 1 : 2,
          // order_type: this.orderModel.order_type,
          // orderType === 'current' ? false : true
          order_amount: order_amount,
          commission: Number(commission),
          time_in_force: this.orderModel.time_in_force,
          expire_time:
            this.orderModel.time_in_force === 6
              ? this.orderModel.expire_time
              : null,
        };
      }
    },
    price: {
      get() {
        if (this.$parent.$parent.selectedOrder) {
          if (
            (this.type == "sell" &&
              this.$parent.$parent.selectedOrder.type == "0" &&
              this.selectedOrder.type == "0") ||
            (this.type == "buy" &&
              this.$parent.$parent.selectedOrder.type == "1" &&
              this.selectedOrder.type == "1")
          ) {
            return this.$parent.$parent.selectedOrder.price;
          } else {
            return this.orderModel.unit_price;
          }
        } else {
          return this.orderModel.unit_price;
        }
      },
      set(val) {
        this.orderModel.unit_price = val;
      },
    },
    quantity: {
      get() {
        return this.orderModel.quantity;
      },
      set(val) {
        this.orderModel.quantity = val;
      },
    },
  },
};
</script>

<style lang="scss">
@import "./OrderForm.scss";
</style>
